/**
 * CONSAI Business App - Sales Management Page
 * Enhanced with item details, searchable dropdown, discount, QR codes, and full editing
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import {
  ShoppingCart, Plus, Trash2, ChevronLeft, ChevronRight, IndianRupee, X,
  ChevronsUpDown, Check, Edit2, Eye, QrCode, Calendar, Percent, User, Package
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import { AetherPage } from '../components/AetherPage';
import PlanExpiredModal from '../components/PlanExpiredModal';
import { GlowCard } from '../components/ui/glow-card';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Sales = () => {
  const { language } = useLanguage();
  const { isPlanExpired } = useAuth();
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [page, setPage] = useState(1);
  const [showPlanExpired, setShowPlanExpired] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDetails, setShowDetails] = useState(null);
  const [editingSale, setEditingSale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [qrCodes, setQrCodes] = useState([]);
  const [defaultQr, setDefaultQr] = useState(null);
  
  // Filters for Add Sale
  const [categoryFilter, setCategoryFilter] = useState('');
  const [itemSearchOpen, setItemSearchOpen] = useState(null); // Track which item row has dropdown open
  
  // New customer inline form
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({ name: '', mobile: '' });
  
  const [formData, setFormData] = useState({
    customer_name: '', 
    customer_id: '', 
    payment_method: 'cash', 
    notes: '',
    sale_date: new Date().toISOString().split('T')[0],
    discount_type: '',
    discount_amount: 0,
    qr_code_id: '',
    qr_code_name: '',
    items: [{ item_name: '', item_id: '', quantity: 1, unit: 'pcs', unit_price: 0 }]
  });

  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/sales?page=${page}&per_page=20`, { withCredentials: true });
      setSales(res.data.sales);
      setTotal(res.data.total);
      setTotalRevenue(res.data.total_revenue);
    } catch (err) {
      toast.error('Failed to load sales');
    }
    setLoading(false);
  // API_URL and axios are module-level constants — safe to omit from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchHelpers = async () => {
    try {
      const [custRes, invRes, qrRes] = await Promise.all([
        axios.get(`${API_URL}/api/customers?per_page=100`, { withCredentials: true }),
        axios.get(`${API_URL}/api/inventory?per_page=100`, { withCredentials: true }),
        axios.get(`${API_URL}/api/qr-codes`, { withCredentials: true }).catch(() => ({ data: { qr_codes: [] } })),
      ]);
      setCustomers(custRes.data.customers || []);
      setInventoryItems(invRes.data.items || []);
      setCategories(invRes.data.categories || []);
      setQrCodes(qrRes.data.qr_codes || []);
      setDefaultQr(qrRes.data.default_qr || null);
    } catch (e) {
      console.warn('[Sales] Failed to load helpers (customers/inventory/qr-codes):', e);
    }
  };

  useEffect(() => { fetchSales(); }, [fetchSales]);

  // Filtered inventory items based on category
  const filteredItems = useMemo(() => {
    if (!categoryFilter) return inventoryItems;
    return inventoryItems.filter(item => item.category === categoryFilter);
  }, [inventoryItems, categoryFilter]);

  const openForm = () => {
    // Check plan expiry before allowing to add sale
    if (isPlanExpired()) {
      setShowPlanExpired(true);
      return;
    }
    fetchHelpers();
    setEditingSale(null);
    setFormData({ 
      customer_name: '', 
      customer_id: '', 
      payment_method: 'cash', 
      notes: '',
      sale_date: new Date().toISOString().split('T')[0],
      discount_type: '',
      discount_amount: 0,
      qr_code_id: '',
      qr_code_name: '',
      items: [{ item_name: '', item_id: '', quantity: 1, unit: 'pcs', unit_price: 0 }] 
    });
    setCategoryFilter('');
    setShowForm(true);
  };

  const openEditSale = async (sale) => {
    await fetchHelpers();
    setEditingSale(sale);
    setFormData({
      customer_name: sale.customer_name || '',
      customer_id: sale.customer_id || '',
      payment_method: sale.payment_method || 'cash',
      notes: sale.notes || '',
      sale_date: sale.sale_date || new Date().toISOString().split('T')[0],
      discount_type: sale.discount_type || '',
      discount_amount: sale.discount_amount || 0,
      qr_code_id: sale.qr_code_id || '',
      qr_code_name: sale.qr_code_name || '',
      items: sale.items?.length > 0 ? sale.items.map(i => ({
        item_id: i.item_id || '',
        item_name: i.item_name || '',
        quantity: i.quantity || 1,
        unit: i.unit || 'pcs',
        unit_price: i.unit_price || 0,
      })) : [{ item_name: '', item_id: '', quantity: 1, unit: 'pcs', unit_price: 0 }]
    });
    setShowForm(true);
  };

  const updateItem = (idx, field, value) => {
    const newItems = [...formData.items];
    newItems[idx] = { ...newItems[idx], [field]: value };
    // Auto-fill price and unit from inventory
    if (field === 'item_id' && value) {
      const inv = inventoryItems.find(i => i.item_id === value);
      if (inv) {
        newItems[idx].item_name = inv.item_name;
        newItems[idx].unit_price = inv.selling_price;
        newItems[idx].unit = inv.unit || 'pcs';
      }
    }
    setFormData({ ...formData, items: newItems });
  };

  const selectInventoryItem = (idx, item) => {
    const newItems = [...formData.items];
    newItems[idx] = {
      ...newItems[idx],
      item_id: item.item_id,
      item_name: item.item_name,
      unit_price: item.selling_price,
      unit: item.unit || 'pcs',
    };
    setFormData({ ...formData, items: newItems });
    setItemSearchOpen(null);
  };

  const addItem = () => setFormData({ ...formData, items: [...formData.items, { item_name: '', item_id: '', quantity: 1, unit: 'pcs', unit_price: 0 }] });
  const removeItem = (idx) => {
    if (formData.items.length <= 1) return;
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== idx) });
  };

  // Calculate totals
  const subtotal = formData.items.reduce((sum, i) => sum + (Number(i.quantity) * Number(i.unit_price)), 0);
  const discountValue = formData.discount_type === 'percentage' 
    ? subtotal * (Number(formData.discount_amount) / 100)
    : (formData.discount_type === 'fixed' ? Number(formData.discount_amount) : 0);
  const saleTotal = subtotal - discountValue;

  // Handle new customer inline
  const handleAddNewCustomer = async () => {
    if (!newCustomerData.name.trim()) {
      toast.error('Customer name is required');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/customers`, {
        name: newCustomerData.name,
        mobile: newCustomerData.mobile || null,
      }, { withCredentials: true });
      
      setCustomers([res.data, ...customers]);
      setFormData({ 
        ...formData, 
        customer_id: res.data.customer_id, 
        customer_name: res.data.name 
      });
      setShowNewCustomer(false);
      setNewCustomerData({ name: '', mobile: '' });
      toast.success('Customer added');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add customer');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validItems = formData.items.filter(i => i.item_name.trim());
    if (validItems.length === 0) { toast.error('Add at least one item'); return; }
    
    try {
      const payload = {
        customer_id: formData.customer_id || null,
        customer_name: formData.customer_name || null,
        items: validItems.map(i => ({ 
          item_id: i.item_id || null, 
          item_name: i.item_name, 
          quantity: Number(i.quantity), 
          unit: i.unit || 'pcs',
          unit_price: Number(i.unit_price) 
        })),
        payment_method: formData.payment_method,
        notes: formData.notes || null,
        sale_date: formData.sale_date,
        discount_type: formData.discount_type || null,
        discount_amount: Number(formData.discount_amount) || 0,
        discount_value: discountValue,
        qr_code_id: (formData.payment_method === 'upi' || formData.payment_method === 'credit') ? formData.qr_code_id : null,
        qr_code_name: (formData.payment_method === 'upi' || formData.payment_method === 'credit') ? formData.qr_code_name : null,
      };
      
      if (editingSale) {
        await axios.put(`${API_URL}/api/sales/${editingSale.sale_id}`, payload, { withCredentials: true });
        toast.success(language === 'hi' ? 'बिक्री अपडेट हुई' : 'Sale updated');
      } else {
        await axios.post(`${API_URL}/api/sales`, payload, { withCredentials: true });
        toast.success(language === 'hi' ? 'बिक्री दर्ज हुई' : 'Sale recorded');
        // Trigger onboarding completion if active
        if (window.triggerOnboardingComplete) window.triggerOnboardingComplete('sale');
      }
      setShowForm(false);
      setEditingSale(null);
      fetchSales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sale?')) return;
    try {
      await axios.delete(`${API_URL}/api/sales/${id}`, { withCredentials: true });
      toast.success('Sale deleted');
      fetchSales();
    } catch (err) { toast.error('Failed'); }
  };

  const fetchSaleDetails = async (saleId) => {
    try {
      const res = await axios.get(`${API_URL}/api/sales/${saleId}`, { withCredentials: true });
      setShowDetails(res.data);
    } catch (err) {
      toast.error('Failed to load sale details');
    }
  };

  // Auto-select default QR when payment method changes
  useEffect(() => {
    if ((formData.payment_method === 'upi' || formData.payment_method === 'credit') && defaultQr && !formData.qr_code_id) {
      setFormData({ ...formData, qr_code_id: defaultQr.qr_id, qr_code_name: defaultQr.qr_name });
    }
  }, [formData.payment_method, defaultQr]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', { 
      timeZone: 'Asia/Kolkata',
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / 20);

  const salesBadge = (
    <span className="bg-[var(--aether-surface-high)] text-white/65 px-2 py-0.5 rounded text-[12px] font-bold" data-testid="sales-count-badge">
      {total}
    </span>
  );
  const salesAddBtn = (
    <button
      onClick={openForm}
      style={{ backgroundColor: '#FFFFFF', color: '#0A0A0B' }}
      className="px-5 py-2 rounded-lg font-bold text-sm flex items-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all"
      data-testid="add-sale-btn"
    >
      <Plus className="h-4 w-4" />
      {language === 'hi' ? 'नई बिक्री' : 'New Sale'}
    </button>
  );

  return (
    <AetherPage
      title={language === 'hi' ? 'बिक्री' : 'Sales'}
      icon={ShoppingCart}
      iconColor="#ffffff"
      titleTestId="sales-title"
      badge={salesBadge}
      rightElement={salesAddBtn}
      mainClassName="px-4 md:px-10 py-6 md:py-10 space-y-8 max-w-[1200px] mx-auto"
    >
          {/* Revenue Overview Card (Aether emerald-glow) */}
          <section className="relative group">
            <div className="absolute -inset-1 bg-gradient-to-r from-[#62FF96]/20 to-transparent blur-xl opacity-60 group-hover:opacity-90 transition-opacity rounded-2xl pointer-events-none" />
            <div className="relative aether-bg-lowest executive-border glow-emerald rounded-xl p-6 md:p-8 flex items-center justify-between overflow-hidden">
              <div className="flex items-center gap-5">
                <div className="w-16 h-16 rounded-full bg-[var(--aether-surface-high)] flex items-center justify-center border border-[#62FF96]/30">
                  <span className="text-[#62FF96] text-2xl font-bold">₹</span>
                </div>
                <div>
                  <p className="label-caps text-white/55 mb-1">{language === 'hi' ? 'कुल राजस्व' : 'Total Revenue'}</p>
                  <h3 className="display-md md:display-lg text-white" data-testid="total-revenue">
                    ₹{totalRevenue.toLocaleString('en-IN')}
                  </h3>
                </div>
              </div>
              <div className="hidden md:flex items-end gap-2">
                <span className="h-10 w-1 bg-[#62FF96]/20 rounded-full"></span>
                <span className="h-14 w-1 bg-[#62FF96]/40 rounded-full"></span>
                <span className="h-8 w-1 bg-[#62FF96]/10 rounded-full"></span>
                <span className="h-12 w-1 bg-[#62FF96]/60 rounded-full"></span>
                <span className="h-16 w-1 bg-[#62FF96] rounded-full"></span>
              </div>
            </div>
          </section>

          {/* Transactions */}
          <section className="space-y-4">
            <h4 className="label-caps text-white/55 mb-2">{language === 'hi' ? 'हाल की लेन-देन' : 'Recent Transactions'}</h4>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-white/70 border-t-transparent rounded-full" />
              </div>
            ) : sales.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-[var(--aether-outline-variant)] rounded-xl">
                <ShoppingCart className="h-12 w-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/55">{language === 'hi' ? 'कोई बिक्री नहीं' : 'No sales recorded yet'}</p>
                <button
                  onClick={openForm}
                  className="mt-4 px-5 py-2 rounded-lg font-bold text-sm bg-white text-[#0A0A0B] hover:opacity-90"
                  data-testid="empty-add-sale-btn"
                >
                  {language === 'hi' ? 'पहली बिक्री जोड़ें' : 'Add your first sale'}
                </button>
              </div>
            ) : (
              <div className="space-y-4" data-testid="sales-list">
                {sales.map((sale) => (
                  <div
                    key={sale.sale_id}
                    className="bg-[var(--aether-surface-low)] executive-border rounded-xl p-5 md:p-6 transition-all duration-300 hover:bg-[var(--aether-surface)] hover:border-[#62FF96]/40 group"
                    data-testid={`sale-card-${sale.sale_id}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-baseline gap-3 flex-wrap">
                        <h5 className="headline-md text-white">₹{sale.total_amount.toLocaleString('en-IN')}</h5>
                        <span className="label-caps text-[10px] px-2 py-0.5 border border-[var(--aether-outline-variant)] rounded text-white/65 uppercase group-hover:border-[#62FF96]/50 group-hover:text-[#62FF96] transition-colors">
                          {sale.payment_method}
                        </span>
                        {sale.discount_value > 0 && (
                          <span className="label-caps text-[10px] px-2 py-0.5 rounded bg-[#62FF96]/10 text-[#62FF96]">
                            -{sale.discount_type === 'percentage' ? `${sale.discount_amount}%` : `₹${sale.discount_value}`}
                          </span>
                        )}
                      </div>
                      <div className="flex gap-3 text-white/40 opacity-50 group-hover:opacity-100 transition-opacity">
                        <button onClick={() => fetchSaleDetails(sale.sale_id)} title="View" data-testid={`view-sale-${sale.sale_id}`} className="hover:text-white">
                          <Eye className="h-5 w-5" />
                        </button>
                        <button onClick={() => openEditSale(sale)} title="Edit" data-testid={`edit-sale-${sale.sale_id}`} className="hover:text-white">
                          <Edit2 className="h-5 w-5" />
                        </button>
                        <button onClick={() => handleDelete(sale.sale_id)} data-testid={`delete-sale-${sale.sale_id}`} className="hover:text-red-400">
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </div>

                    {sale.customer_name && (
                      <div className="flex items-center gap-2 mb-3">
                        <User className="h-4 w-4 text-white/55" />
                        <span className="text-white/85">{sale.customer_name}</span>
                      </div>
                    )}

                    <div className="bg-[var(--aether-surface-lowest)]/60 rounded-lg p-3 border border-[var(--aether-outline-variant)]/30 space-y-1.5 group-hover:bg-black/30 transition-colors">
                      {sale.items?.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-white/65">
                            {item.item_name} ({item.quantity} {item.unit || 'pcs'} × ₹{item.unit_price})
                          </span>
                          <span className="font-bold text-white">₹{item.total || (item.quantity * item.unit_price)}</span>
                        </div>
                      ))}
                      {sale.items?.length > 3 && (
                        <p className="text-xs text-white/35">+{sale.items.length - 3} more items</p>
                      )}
                    </div>

                    <div className="mt-3 text-white/40 text-xs font-medium flex items-center gap-2">
                      <Calendar className="h-3.5 w-3.5" />
                      {formatDate(sale.created_at)}
                      {sale.qr_code_name && (
                        <>
                          <span className="opacity-50">•</span>
                          <span className="inline-flex items-center gap-1"><QrCode className="h-3 w-3" />{sale.qr_code_name}</span>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="flex justify-center gap-2 pt-4">
                <button
                  disabled={page <= 1} onClick={() => setPage(page - 1)}
                  className="p-2 border border-[var(--aether-outline-variant)] rounded-lg text-white disabled:opacity-30 hover:bg-white/5"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="flex items-center text-sm text-white/65 px-3">{page} / {totalPages}</span>
                <button
                  disabled={page >= totalPages} onClick={() => setPage(page + 1)}
                  className="p-2 border border-[var(--aether-outline-variant)] rounded-lg text-white disabled:opacity-30 hover:bg-white/5"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </section>

      {/* Add/Edit Sale Dialog - Mobile centered modal */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto mx-4 my-4 max-sm:mx-3 max-sm:my-6 max-sm:rounded-xl max-sm:max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="font-manrope">
              {editingSale ? (language === 'hi' ? 'बिक्री अपडेट' : 'Edit Sale') : (language === 'hi' ? 'नई बिक्री' : 'New Sale')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Sale Date */}
            <div>
              <label className="text-xs text-slate-500 flex items-center gap-1">
                <Calendar className="h-3 w-3" /> {language === 'hi' ? 'तारीख' : 'Date'}
              </label>
              <Input 
                type="date" 
                value={formData.sale_date} 
                onChange={(e) => setFormData({ ...formData, sale_date: e.target.value })}
                className=""
              />
            </div>
            
            {/* Customer Selection with Add New */}
            <div>
              <label className="text-xs text-slate-500">{language === 'hi' ? 'ग्राहक' : 'Customer'}</label>
              {showNewCustomer ? (
                <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
                  <Input 
                    placeholder="Customer Name *" 
                    value={newCustomerData.name}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                  />
                  <Input 
                    placeholder="Mobile (optional)" 
                    value={newCustomerData.mobile}
                    onChange={(e) => setNewCustomerData({ ...newCustomerData, mobile: e.target.value })}
                  />
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={handleAddNewCustomer}>Save & Select</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setShowNewCustomer(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Select value={formData.customer_id} onValueChange={(v) => {
                    const cust = customers.find(c => c.customer_id === v);
                    setFormData({ ...formData, customer_id: v, customer_name: cust?.name || '' });
                  }}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Cash Sale (No Customer)</SelectItem>
                      {customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="sm" onClick={() => setShowNewCustomer(true)}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Category Filter */}
            <div>
              <label className="text-xs text-slate-500">{language === 'hi' ? 'श्रेणी फ़िल्टर' : 'Category Filter'}</label>
              <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="All Categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Items */}
            <div className="space-y-2">
              <label className="text-xs text-slate-500">{language === 'hi' ? 'आइटम' : 'Items'}</label>
              {formData.items.map((item, idx) => (
                <div key={`item-row-${idx}`} className="flex gap-2 items-start p-2 bg-slate-50 rounded-lg">
                  <div className="flex-1 space-y-2">
                    {/* Searchable Item Dropdown */}
                    <Popover open={itemSearchOpen === idx} onOpenChange={(open) => setItemSearchOpen(open ? idx : null)}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="w-full justify-between text-left font-normal">
                          {item.item_name || "Select item..."}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-full p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Search items..." />
                          <CommandList>
                            <CommandEmpty>No items found.</CommandEmpty>
                            <CommandGroup>
                              {filteredItems.map((invItem) => (
                                <CommandItem
                                  key={invItem.item_id}
                                  value={invItem.item_name}
                                  onSelect={() => selectInventoryItem(idx, invItem)}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${item.item_id === invItem.item_id ? "opacity-100" : "opacity-0"}`} />
                                  <div className="flex-1">
                                    <div className="font-medium">{invItem.item_name}</div>
                                    <div className="text-xs text-slate-500">
                                      Stock: {invItem.current_quantity} {invItem.unit} • ₹{invItem.selling_price}
                                    </div>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Input 
                          type="number" 
                          placeholder="Qty" 
                          value={item.quantity} 
                          onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      <div className="w-20">
                        <Input 
                          value={item.unit} 
                          readOnly 
                          className="text-sm bg-slate-100 text-center" 
                          title="Unit (from item)"
                        />
                      </div>
                      <div className="flex-1">
                        <Input 
                          type="number" 
                          placeholder="Price" 
                          value={item.unit_price} 
                          onChange={(e) => updateItem(idx, 'unit_price', e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                  {formData.items.length > 1 && (
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(idx)} className="mt-0.5">
                      <X className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" variant="outline" size="sm" onClick={addItem} className="w-full">
                <Plus className="h-4 w-4 mr-1" />{language === 'hi' ? 'आइटम जोड़ें' : 'Add Item'}
              </Button>
            </div>

            {/* Discount */}
            <div>
              <label className="text-xs text-slate-500 flex items-center gap-1">
                <Percent className="h-3 w-3" /> {language === 'hi' ? 'छूट' : 'Discount'}
              </label>
              <div className="flex gap-2">
                <Input 
                  type="number" 
                  placeholder="Amount" 
                  value={formData.discount_amount}
                  onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })}
                  className="flex-1"
                />
                <Select 
                  value={formData.discount_type || 'none'} 
                  onValueChange={(v) => setFormData({ ...formData, discount_type: v === 'none' ? '' : v })}
                >
                  <SelectTrigger className="w-24"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="percentage">%</SelectItem>
                    <SelectItem value="fixed">₹</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="text-xs text-slate-500">{language === 'hi' ? 'भुगतान' : 'Payment'}</label>
              <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">{language === 'hi' ? 'नकद' : 'Cash'}</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">{language === 'hi' ? 'कार्ड' : 'Card'}</SelectItem>
                  <SelectItem value="credit">{language === 'hi' ? 'उधार' : 'Credit'}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* QR Code Selection (for UPI/Credit) */}
            {(formData.payment_method === 'upi' || formData.payment_method === 'credit') && qrCodes.length > 0 && (
              <div>
                <label className="text-xs text-slate-500 flex items-center gap-1">
                  <QrCode className="h-3 w-3" /> {language === 'hi' ? 'QR कोड' : 'QR Code'}
                </label>
                <Select 
                  value={formData.qr_code_id} 
                  onValueChange={(v) => {
                    const qr = qrCodes.find(q => q.qr_id === v);
                    setFormData({ ...formData, qr_code_id: v, qr_code_name: qr?.qr_name || '' });
                  }}
                >
                  <SelectTrigger><SelectValue placeholder="Select QR Code" /></SelectTrigger>
                  <SelectContent>
                    {qrCodes.map(qr => (
                      <SelectItem key={qr.qr_id} value={qr.qr_id}>
                        {qr.qr_name} {qr.is_default && '(Default)'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Input placeholder={language === 'hi' ? 'नोट्स (वैकल्पिक)' : 'Notes (optional)'} value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="" />

            {/* Totals Summary */}
            <div className="bg-slate-100 rounded-lg p-3 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Subtotal:</span>
                <span>₹{subtotal.toLocaleString('en-IN')}</span>
              </div>
              {discountValue > 0 && (
                <div className="flex justify-between text-sm text-green-600">
                  <span>Discount:</span>
                  <span>-₹{discountValue.toLocaleString('en-IN')}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg pt-1 border-t">
                <span>{language === 'hi' ? 'कुल' : 'Total'}:</span>
                <span data-testid="sale-form-total">₹{saleTotal.toLocaleString('en-IN')}</span>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="">
                {language === 'hi' ? 'रद्द करें' : 'Cancel'}
              </Button>
              <Button type="submit" className="bg-[#021945] hover:bg-[#021945]/90" data-testid="sale-submit-btn">
                {editingSale ? (language === 'hi' ? 'अपडेट' : 'Update') : (language === 'hi' ? 'सहेजें' : 'Record Sale')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Sale Details Dialog */}
      <Dialog open={!!showDetails} onOpenChange={() => setShowDetails(null)}>
        <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">{language === 'hi' ? 'बिक्री विवरण' : 'Sale Details'}</DialogTitle>
          </DialogHeader>
          
          {showDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-slate-500">Customer</p>
                  <p className="font-medium">{showDetails.customer_name || 'Cash Sale'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Date</p>
                  <p className="font-medium">{formatDate(showDetails.created_at)}</p>
                </div>
                <div>
                  <p className="text-slate-500">Payment</p>
                  <p className="font-medium capitalize">{showDetails.payment_method}</p>
                </div>
                {showDetails.qr_code_name && (
                  <div>
                    <p className="text-slate-500">QR Code</p>
                    <p className="font-medium">{showDetails.qr_code_name}</p>
                  </div>
                )}
              </div>

              <div>
                <h4 className="font-semibold mb-2">{language === 'hi' ? 'आइटम' : 'Items'}</h4>
                <div className="space-y-2 bg-slate-50 rounded-lg p-3">
                  {showDetails.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between">
                      <div>
                        <p className="font-medium">{item.item_name}</p>
                        <p className="text-xs text-slate-500">
                          {item.quantity} {item.unit || 'pcs'} × ₹{item.unit_price}
                        </p>
                      </div>
                      <p className="font-medium">₹{item.total || (item.quantity * item.unit_price)}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t pt-3 space-y-1">
                {showDetails.subtotal && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Subtotal:</span>
                    <span>₹{showDetails.subtotal?.toLocaleString('en-IN')}</span>
                  </div>
                )}
                {showDetails.discount_value > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount ({showDetails.discount_type === 'percentage' ? `${showDetails.discount_amount}%` : 'Fixed'}):</span>
                    <span>-₹{showDetails.discount_value?.toLocaleString('en-IN')}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg">
                  <span>{language === 'hi' ? 'कुल' : 'Total'}:</span>
                  <span>₹{showDetails.total_amount?.toLocaleString('en-IN')}</span>
                </div>
              </div>

              {/* QR Code Display */}
              {showDetails.qr_image_url && (
                <div className="flex flex-col items-center pt-2 border-t">
                  <p className="text-xs text-slate-500 mb-2">{language === 'hi' ? 'स्कैन करके भुगतान करें' : 'Scan to Pay'}</p>
                  <img src={showDetails.qr_image_url} alt="Payment QR" className="w-24 h-24 rounded-lg border" />
                  {showDetails.qr_code_name && (
                    <p className="text-sm font-medium text-slate-700 mt-1">{showDetails.qr_code_name}</p>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PlanExpiredModal open={showPlanExpired} onClose={() => setShowPlanExpired(false)} />
    </AetherPage>
  );
};

export default Sales;
